<script lang="ts" setup>
import { ref, inject, computed } from 'vue';
import Draggable from 'vuedraggable';
import CResourceWrapper from './ResourceWrapper.vue';

const { $events } = useEvents();

const getFileOptions = inject<any>('getFileOptions');

const props = withDefaults(defineProps<{
    modelValue: FileResource[];
    media: string;
    type?: 'folders' | 'files';
    scope?: string;
    selected: FileResource[];
    prependItems?: FileResource[];
}>(), {
    scope: 'public',
    type: null,
    prependItems: null
});

const modal = inject('modal');

const emit = defineEmits<{
    (e: 'update:modelValue', val: FileResource[]): void;
    (e: 'change-folder', scope: string, folder: number): void;
    (e: 'select-item', item: FileResource, append?: boolean, preventRemove?: boolean, ev?: PointerEvent): void;
    (e: 'edit-item', item: FileResource, event: any): void;
}>();

const select = (item: FileResource, e: any, append?: boolean): void =>
{
    emit('select-item', item, append ?? e?.ctrlKey, null, e);
};

const setFolder = (scope: string, folder: number): void =>
{
    if (scope !== 'selected')
        emit('change-folder', scope, folder);
};

const isDisabled = (item: FileResource): boolean =>
{
    return props.media !== 'files' && item.resourceType == 'file' && item.media.indexOf(props.media) === -1;
};

const execute = (item: FileResource, e: any): void =>
{
    const { resourceType } = item;

    if (resourceType === 'file' && !isDisabled(item))
        $events.$emit('filemanager::callback::value', [item]);

    if (resourceType == 'folder')
        setFolder(props.scope, item.id);
};

const mouseClick = useMouseClick();
const bindItem: any = inject('bindItem');

const onClick = (item: FileResource, e: any): void =>
{
    mouseClick(
        () => select(item, e),
        () => execute(item, e)
    );
};

const isActive = ({ id, resourceType }: FileResource): boolean =>
{
    const index = props.selected.findIndex?.((el: FileResource) =>
    {
        return el.id === id && el.resourceType === resourceType;
    });

    return index >= 0;
};

// Dropdown actions
const onSelect = (item: FileResource, e: any): void =>
{
    select(item, e, true);
};

const canSort = (): boolean =>
{
    return props.scope === 'selected';
};

const items = computed({
    get(): FileResource[]
    {
        if (props.prependItems)
            return [...props.prependItems, ...props.modelValue];
        else
            return props.modelValue;
    },
    set(value: FileResource[])
    {
        emit('update:modelValue', value);
    }
});

const imageErrors = ref<number[]>([]);
const onImageError = (index: number) =>
{
    imageErrors.value.push(index);
};
</script>

<template>
    <div class="resource-list" :class="[type && `resource-list-${type}`]">
        <h4 v-if="type" class="mt-0 mb-3 pb-1">
            <template v-if="type === 'folders'">
                {{ $t('[[[Foldery]]]') }}
            </template>
            <template v-else-if="type === 'files'">
                {{ $t('[[[Pliki]]]') }}
            </template>
        </h4>
        <div class="wrapper">
            <template v-if="items?.length || prependItems">
                <Draggable
                    v-model="items"
                    :item-key="(element: FileResource) => `${element.resourceType}:${element.id}`"
                    draggable=".draggable"
                    :move="canSort"
                >
                    <template #item="{ element: item, index }">
                        <template v-if="prependItems && index < prependItems.length">
                            <CResourceWrapper
                                v-for="(prependItem, prependItemIndex) in prependItems"
                                :key="prependItemIndex"
                                :resource="prependItem"
                                @click="prependItem.onClick?.()"
                            >
                                <template #icon>
                                    <i :class="prependItem.icon" />
                                </template>
                            </CResourceWrapper>
                        </template>
                        <CResourceWrapper
                            v-else
                            v-lightbox="{ dbClick: true, inactive: !item.isImage || modal }"
                            :resource="item"
                            :active="isActive(item)"
                            :disabled="isDisabled(item)"
                            :class="{ 'active': isActive(item), 'draggable': canSort(), 'inactive': isDisabled(item) }"
                            v-bind="bindItem({ id: item.isImage && !modal ? 'preview' : null }, item)"
                            :checkbox="!!selected.length"
                            :checked="selected?.findIndex?.(el => el.id === item.id) !== -1"
                            @click="!isDisabled(item) && onClick(item, $event)"
                            @on-select="onSelect"
                        >
                            <!-- Resource icon -->
                            <template #icon>
                                <i class="fas" :class="[getFileOptions(item).classes]" />
                            </template>

                            <div v-if="item.resourceType === 'file'" class="w-100 flex-fill mt-2 rounded-2 overflow-hidden bg-white d-flex justify-content-center align-items-center">
                                <template v-if="item.isImage">
                                    <div class="d-flex justify-content-center align-items-center">
                                        <img
                                            v-show="!imageErrors.includes(index)"
                                            :src="$filters.image(item.url, 'thumb')"
                                            :alt="item.name"
                                            class="thumb"
                                            @error="onImageError(index)"
                                        >

                                        <div v-if="imageErrors.includes(index)" class="ratio ratio-1x1">
                                            <i class="d-flex align-items-center justify-content-center far fa-image-slash text-secondary" />
                                        </div>
                                    </div>
                                </template>
                                <div v-else>
                                    <div class="ratio ratio-1x1">
                                        <i class="far d-flex align-items-center justify-content-center text-secondary" :class="[getFileOptions(item).classes]" />
                                    </div>
                                </div>
                            </div>
                        </CResourceWrapper>
                    </template>
                </Draggable>
            </template>
            <p v-else>
                {{ $t('[[[Nie znaleziono pasujących elementów.]]]') }}
            </p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.ratio {
    i {
        font-size: 80px;
    }
}
img {
    -webkit-user-drag: none;
}
.inactive {
    opacity: 0.5;
    cursor: not-allowed;
}
</style>

<style lang="scss">
.filemanager {
    --thumb-gap: 1.25rem;
    --thumb-size: 10rem;

    img.thumb {
        max-width: var(--thumb-size);
            max-height: var(--thumb-size);;
    }

    &-big-thumbs {
        --thumb-size: 13rem;
    }

    .resource-list {
        .wrapper {
            // draggable
            > div {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(var(--thumb-size), 1fr));
                gap: var(--thumb-gap);
            }
        }
    }
}
</style>
